import { useEffect } from 'react'
import type { User } from 'firebase/auth'
import { getFirebaseAuth, isFirebaseConfigured } from '../config/firebase'
import {
  isAllowedEmailDomain,
  loadFirebaseAuthSdk,
} from './authFirebaseProviders'
import {
  isLegacyDemoProfileUid,
  isRestorableDemoProfile,
} from './authDemoProfiles'
import type { ApprovalStatus, UserProfile } from './authTypes'

interface UseAuthStateInitializationOptions {
  isDemoUser: boolean
  setCurrentUser: (user: User | null) => void
  setUserProfile: (
    profile:
      | UserProfile
      | null
      | ((current: UserProfile | null) => UserProfile | null)
  ) => void
  setIsGlobalAdmin: (value: boolean) => void
  setIsDemoUser: (value: boolean) => void
  setApprovalStatus: (status: ApprovalStatus) => void
  setApprovalError: (message: string | null) => void
  setLoading: (value: boolean) => void
  deriveIsGlobalAdmin: (email?: string | null, profileFlag?: boolean) => boolean
  createOrUpdateUserProfile: (user: User) => void
}

export function useAuthStateInitialization({
  isDemoUser,
  setCurrentUser,
  setUserProfile,
  setIsGlobalAdmin,
  setIsDemoUser,
  setApprovalStatus,
  setApprovalError,
  setLoading,
  deriveIsGlobalAdmin,
  createOrUpdateUserProfile,
}: UseAuthStateInitializationOptions) {
  useEffect(() => {
    const savedDemoUser = localStorage.getItem('isDemoUser')
    const savedProfile = localStorage.getItem('userProfile')
    const savedAuthUser = localStorage.getItem('auth_user')
    const savedAuthToken = localStorage.getItem('auth_token')

    if (savedDemoUser === 'true' && savedProfile) {
      try {
        const parsedProfile = JSON.parse(savedProfile)
        const profileUid = String(parsedProfile.uid || '')

        if (isRestorableDemoProfile(profileUid, savedAuthToken)) {
          if (isLegacyDemoProfileUid(profileUid)) {
            localStorage.removeItem('auth_token')
            localStorage.removeItem('auth_user')
            localStorage.removeItem('refresh_token')
          }
          setUserProfile(parsedProfile)
          setIsGlobalAdmin(Boolean(parsedProfile.isGlobalAdmin))
          setIsDemoUser(true)
          setApprovalStatus('approved')
          setApprovalError(null)
          setLoading(false)
          return
        }
      } catch (error) {
        console.error('데모 유저 복원 실패:', error)
        localStorage.removeItem('isDemoUser')
      }
    }

    const backendCredentialProfile = restoreBackendCredentialProfile(
      savedProfile,
      savedAuthUser,
      savedAuthToken
    )
    if (backendCredentialProfile) {
      setCurrentUser(null)
      setUserProfile(backendCredentialProfile)
      setIsGlobalAdmin(Boolean(backendCredentialProfile.isGlobalAdmin))
      setIsDemoUser(false)
      setApprovalStatus('approved')
      setApprovalError(null)
      setLoading(false)
      return
    }

    if (!isFirebaseConfigured) {
      setLoading(false)
      return () => {}
    }

    let isActive = true
    let unsubscribe = () => {}

    const initializeAuthState = async () => {
      try {
        const auth = await getFirebaseAuth()
        if (!isActive) return

        if (!auth) {
          setLoading(false)
          return
        }

        const { onAuthStateChanged, getRedirectResult, signOut } =
          await loadFirebaseAuthSdk()
        if (!isActive) return

        let rejectedRedirectUid: string | null = null
        let redirectErrorMessage: string | null = null

        try {
          const result = await getRedirectResult(auth)
          if (result) {
            if (!isAllowedEmailDomain(result.user.email)) {
              rejectedRedirectUid = result.user.uid
              await signOut(auth)
              throw new Error(
                `로그인할 수 없는 계정입니다. (현재: ${result.user.email})`
              )
            }
            createOrUpdateUserProfile(result.user)
          }
        } catch (error) {
          redirectErrorMessage =
            error instanceof Error
              ? error.message
              : '리다이렉트 로그인에 실패했습니다.'
          setApprovalStatus('error')
          setApprovalError(redirectErrorMessage)
          console.error('리다이렉트 로그인 실패:', error)
        }

        if (!isActive) return

        unsubscribe = onAuthStateChanged(auth, (user) => {
          if (user && user.uid === rejectedRedirectUid) {
            setCurrentUser(null)
            setUserProfile(null)
            setIsGlobalAdmin(false)
            setApprovalStatus('error')
            setApprovalError(
              redirectErrorMessage || '리다이렉트 로그인에 실패했습니다.'
            )
            localStorage.removeItem('userProfile')
            setLoading(false)
            return
          }

          setCurrentUser(user)

          if (user) {
            restoreProfileForFirebaseUser(
              user,
              deriveIsGlobalAdmin,
              createOrUpdateUserProfile,
              {
                setUserProfile,
                setIsGlobalAdmin,
              }
            )
          } else if (!isDemoUser) {
            setUserProfile(null)
            setIsGlobalAdmin(false)
            if (redirectErrorMessage) {
              setApprovalStatus('error')
              setApprovalError(redirectErrorMessage)
            } else {
              setApprovalStatus('unknown')
              setApprovalError(null)
            }
            localStorage.removeItem('userProfile')
          }

          setLoading(false)
        })
      } catch (error) {
        console.error('Firebase 인증 초기화 실패:', error)
        if (isActive) {
          setLoading(false)
        }
      }
    }

    void initializeAuthState()

    return () => {
      isActive = false
      unsubscribe()
    }
  }, [])
}

function restoreBackendCredentialProfile(
  savedProfile: string | null,
  savedAuthUser: string | null,
  savedAuthToken: string | null
): UserProfile | null {
  if (!savedAuthToken) return null

  if (isJwtExpired(savedAuthToken)) {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('userProfile')
    return null
  }

  const rawProfile = savedProfile || savedAuthUser
  if (!rawProfile) return null

  try {
    const parsedProfile = JSON.parse(rawProfile)
    const email = parsedProfile?.email
    const uid = parsedProfile?.uid || email
    if (!uid || !email) return null
    return {
      ...parsedProfile,
      uid,
      email,
      role: parsedProfile.role || 'user',
      isGlobalAdmin: Boolean(parsedProfile.isGlobalAdmin),
      preferences: {
        theme: 'light',
        language: 'ko',
        ...(parsedProfile.preferences || {}),
      },
    }
  } catch (error) {
    console.error('저장된 백엔드 로그인 프로필 복원 실패:', error)
    return null
  }
}

function isJwtExpired(token: string): boolean {
  const parts = token.split('.')
  if (parts.length < 2) return false

  try {
    const payloadPart = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const paddedPayload = payloadPart.padEnd(
      Math.ceil(payloadPart.length / 4) * 4,
      '='
    )
    const payload = JSON.parse(atob(paddedPayload))
    if (typeof payload.exp !== 'number') return false
    return payload.exp * 1000 <= Date.now()
  } catch {
    return false
  }
}

function restoreProfileForFirebaseUser(
  user: User,
  deriveIsGlobalAdmin: (
    email?: string | null,
    profileFlag?: boolean
  ) => boolean,
  createOrUpdateUserProfile: (user: User) => void,
  setters: {
    setUserProfile: (profile: UserProfile | null) => void
    setIsGlobalAdmin: (value: boolean) => void
  }
) {
  const savedProfile = localStorage.getItem('userProfile')
  if (!savedProfile) {
    createOrUpdateUserProfile(user)
    return
  }

  try {
    const parsedProfile = JSON.parse(savedProfile)
    if (parsedProfile.uid === user.uid) {
      setters.setUserProfile({
        ...parsedProfile,
        email: user.email,
        photoURL: user.photoURL,
        lastLoginAt: new Date(),
      })
      setters.setIsGlobalAdmin(
        deriveIsGlobalAdmin(user.email, parsedProfile.isGlobalAdmin)
      )
    } else {
      createOrUpdateUserProfile(user)
    }
  } catch (error) {
    console.error('저장된 프로필 복원 실패:', error)
    createOrUpdateUserProfile(user)
  }
}
